import { CalendarDays, Clock } from "lucide-react";
import { useGetTimeAnalyticsQuery } from "@/redux/services/dashboard/dashboard.api";
import { cn } from "@/lib/utils";
import { reportCard } from "./reportCard";

interface PeakTimesSectionProps {
  restaurantId: string;
  startDate: string;
  endDate: string;
}

const DAY_FR: Record<string, string> = {
  monday: "Lundi",
  tuesday: "Mardi",
  wednesday: "Mercredi",
  thursday: "Jeudi",
  friday: "Vendredi",
  saturday: "Samedi",
  sunday: "Dimanche",
};

/**
 * "When am I busy?" — surfaces the busiest hour and best weekday over the
 * period so managers can staff accordingly (SFRESTO-87). Shares the
 * time-analytics query with the charts section, so RTK Query serves it from
 * cache rather than refetching.
 */
function PeakTimesSection({
  restaurantId,
  startDate,
  endDate,
}: PeakTimesSectionProps) {
  // compare: true mirrors ReportChartsSection so both share one cached request;
  // the previous-period data is unused here (peaks come from the current period).
  const { data } = useGetTimeAnalyticsQuery(
    {
      restaurant_id: restaurantId,
      start_date: startDate,
      end_date: endDate,
      compare: true,
    },
    { skip: !restaurantId }
  );

  const current = data?.data.current;
  const hours = current?.hourly_distribution ?? [];
  const days = current?.day_of_week ?? [];

  const peakHour = hours.reduce<(typeof hours)[number] | null>(
    (best, h) => (best === null || h.orders > best.orders ? h : best),
    null
  );
  const bestDay = days.reduce<(typeof days)[number] | null>(
    (best, d) =>
      best === null || d.average_orders > best.average_orders ? d : best,
    null
  );

  const hasPeakHour = peakHour !== null && peakHour.orders > 0;
  const hasBestDay = bestDay !== null && bestDay.average_orders > 0;

  if (!hasPeakHour && !hasBestDay) {
    return null;
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {hasPeakHour && (
        <PeakCard
          icon={Clock}
          accent="#f0590e"
          label="Heure de pointe"
          value={`${peakHour!.hour}h – ${(peakHour!.hour + 1) % 24}h`}
          hint={`${peakHour!.orders} commande${peakHour!.orders > 1 ? "s" : ""} sur le créneau`}
        />
      )}
      {hasBestDay && (
        <PeakCard
          icon={CalendarDays}
          accent="#066a69"
          label="Meilleur jour"
          value={DAY_FR[bestDay!.day.toLowerCase()] ?? bestDay!.day}
          hint={`${bestDay!.average_orders.toFixed(1)} commandes en moyenne`}
        />
      )}
    </div>
  );
}

function PeakCard({
  icon: Icon,
  accent,
  label,
  value,
  hint,
}: {
  icon: React.ElementType;
  accent: string;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className={cn("flex items-center gap-4 p-5", reportCard)}>
      <div
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl"
        style={{ backgroundColor: `${accent}1a` }}
      >
        <Icon className="h-6 w-6" style={{ color: accent }} />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        <p className="truncate text-xl font-semibold text-card-foreground">
          {value}
        </p>
        <p className="truncate text-xs text-muted-foreground">{hint}</p>
      </div>
    </div>
  );
}

export default PeakTimesSection;
